import { formatCurrency, formatNumberForCurrencyLocale } from 'src/utils/currencyFormatting';

/**
 * Privacy mode masking primitives.
 *
 * This module is deliberately **pure and context-free**: it takes a boolean and returns
 * formatters. That is what makes it unit-testable without React and reusable by
 * `CompositionDonut`, which receives the boolean as a prop rather than from context.
 *
 * **The load-bearing rule: substitute at the innermost numeric formatting call, never at
 * the composed string.** Every signed site in the codebase composes its sign *outside* the
 * `formatCurrency` call, so `+$1,234` becomes `+••••••` for free and signs, colours and
 * layout survive with no explicit exception list. Following the rule also leaves every
 * percentage untouched, which is a hard product requirement (percentages are never masked).
 *
 * Masking is text replacement, never a CSS blur: the real digits must never reach the DOM,
 * the accessibility tree, or copied page text.
 */

/**
 * Six U+2022 bullets. Fixed width so the lightweight-charts price-scale gutter does not
 * jitter between privacy-on and privacy-off, and digit-free so DOM-absence assertions
 * against sentinel values are meaningful.
 */
export const MASKED_VALUE = '••••••';

export interface PrivacyFormatters {
  privacyMode: boolean;
  formatCurrency: typeof formatCurrency;
  formatNumberForCurrencyLocale: typeof formatNumberForCurrencyLocale;
  /** Masks an already-formatted numeric string (share counts, axis labels, `toFixed` output). */
  maskNumeric: (formatted: string) => string;
}

const visibleFormatters: PrivacyFormatters = {
  privacyMode: false,
  formatCurrency,
  formatNumberForCurrencyLocale,
  maskNumeric: (formatted: string) => formatted
};

const maskedFormatters: PrivacyFormatters = {
  privacyMode: true,
  formatCurrency: () => MASKED_VALUE,
  formatNumberForCurrencyLocale: () => MASKED_VALUE,
  maskNumeric: () => MASKED_VALUE
};

export const buildPrivacyFormatters = (privacyMode: boolean): PrivacyFormatters =>
  (privacyMode ? maskedFormatters : visibleFormatters);
